import { Collapse } from '@material-ui/core';
import { Col } from 'components/Col';
import CustomIconButton from 'components/CustomIconButton/CustomIconButton';
import Dislike from 'components/Icons/DislikeIcon';
import HistoryRoundedIcon from 'components/Icons/HistoryRoundedIcon';
import KeyboardArrowDownIcon from 'components/Icons/KeyboardArrowDownIcon';
import LikeIcon from 'components/Icons/LikeIcon';
import { Row } from 'components/Row';
import { ProjetosContext } from 'contexts/projetos/ProjetosContext';
import { getApis } from 'library/Apis';
import {
  DocumentoProjeto,
  EtapaProjeto,
  indexOfEtapa,
  ProjetoType,
  StatusEtapa,
} from 'models/projeto';
import moment from 'moment';
import { REVISAO_SCROLL_ID } from 'pages/FluxoProjetos/Conteudo/etapas/revisaoFinalizacao';
import { useCallback, useContext, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { scroller } from 'react-scroll';
import { openModal } from 'store/modules/ModalActions';
import { typeIsImage, typeIsVideo } from 'utils/FileUtils';
import { getMergedClassNames } from 'utils/HTMLUtils';
import languageValues from 'utils/LanguageUtils';
import { getUniqueKey } from 'utils/NumberUtils';
import { etapasConfig } from 'utils/projeto';
import { getProjectFilesByEtapa } from 'utils/ProjetoUtils';
import SnackbarUtils from 'utils/SnackbarUtils';
import { generateVideoThumbnail } from 'utils/videoUtils';
import FinalizarEtapaModal from '../finalizarEtapaModal/finalizarEtapaModal';
import HistoricoModal from '../historicoModal/historicoModal';
import styles from './reviewEtapa.scss';
const { rootClassName } = styles;

export default function ReviewEtapa(props: {
  etapa: EtapaProjeto;
  flgInfantil: boolean;
}) {
  const id = useMemo(() => `${props.etapa}-toggler`, [props.etapa]);
  const scrollToNext = useRef(false);
  const auth = useSelector((state: any) => state.auth);

  const canApprove = useMemo(
    () => auth.userPermissions.projetos.approve,
    [auth]
  );

  const canReprove = useMemo(
    () => auth.userPermissions.projetos.reprove,
    [auth]
  );

  const {
    state: { projeto, options, statusEtapas, disabledFields },
    avaliarEtapa,
  } = useContext(ProjetosContext);

  const status = useMemo(() => {
    const etp = projeto.avaliacoes?.find(
      (av) => av.etapa === indexOfEtapa(props.etapa)
    );
    if (etp) return etp.status as StatusEtapa;

    return StatusEtapa.SemPreenchimento;
  }, [projeto.avaliacoes, props.etapa]);

  const [opened, setOpened] = useState<boolean>(
    (canApprove || canReprove) && status === StatusEtapa.AguardandoAprovacao
  );
  const controller = useRef(new AbortController());

  const d = useDispatch();

  const fields = useMemo(() => {
    return Object.keys(etapasConfig[props.etapa].fields)
      .filter((campo) => {
        return !['flgInfantil', 'consultaPublica'].includes(campo);
      })
      .map<{
        label: string;
        value: string;
      }>((campo) => {
        const k = campo as keyof ProjetoType;
        const inputs = languageValues.inputs as { [k: string]: any };
        let obj = k in inputs ? inputs[k] : '';
        if (k === 'nome' && props.etapa === EtapaProjeto.infosIniciais) {
          obj = inputs.preTitulo;
        }
        let label = '';
        let value = '';
        // Label
        if (typeof obj === 'string') {
          label = obj;
        } else if (typeof obj.label === 'function') {
          label = obj?.label(props.flgInfantil) ?? '';
        } else {
          label = obj?.label ?? '';
        }
        // Value
        if (campo in projeto) {
          const getRealValue = (value: any) => {
            const { keyOptions, keyValue, keyLabel } =
              etapasConfig[props.etapa].fields[k]!;
            if (keyOptions && keyValue && keyLabel) {
              const item: any = Array.from(
                options[keyOptions].options as any
              )?.find((opt: any) => opt[keyValue] === value);
              if (item && typeof keyLabel === 'string') {
                return item[keyLabel];
              }
              if (item && typeof keyLabel === 'function') {
                return keyLabel(item);
              }
              // Condiç˜qo isArray colocada paliativamente...
              // para Não remover o valor das variáveis que não sejam array
              if (!item && Array.isArray(projeto[k])) {
                return undefined;
              }
            } else if (
              typeof value === 'boolean' ||
              [
                'direitoImagem',
                'consultaPublica',
                'cienciaCordenador',
              ].includes(campo)
            ) {
              return languageValues.constants.yesNo(value);
            }
            return String(value);
          };
          if (
            typeof projeto[k] === 'boolean' ||
            typeof projeto[k] === 'string' ||
            typeof projeto[k] === 'number' ||
            ['direitoImagem', 'consultaPublica', 'cienciaCordenador'].includes(
              campo
            )
          ) {
            value = getRealValue(projeto[k]);
          } else if (Array.isArray(projeto[k])) {
            value = Array.from(projeto[k] as any[])
              .map((value) => getRealValue(value))
              .filter((r) => Boolean(r))
              .join(', ');
          } else if (
            (!projeto[k] || !String(projeto[k]).length) &&
            !etapasConfig[props.etapa].fields[k]?.required
          ) {
            value = languageValues.pages.projetos.notFullfilled;
          }
        }
        return {
          value,
          label,
        };
      });
  }, [options, projeto, props.etapa, props.flgInfantil]);

  const openModalRevisao = useCallback(
    (aprovar: boolean) => {
      const modalKey = getUniqueKey();
      d(
        openModal(FinalizarEtapaModal, {
          modalKey,
          flgInfantil: props.flgInfantil,
          etapa: props.etapa,
          title:
            languageValues.pages.projetos.actions[
            aprovar ? 'approve' : 'attentionPoint'
            ],
          variant: aprovar ? 'default' : 'error',
          onSubmit: (justificativa) => {
            return avaliarEtapa({
              etapa: props.etapa,
              comentario: justificativa,
              status: aprovar
                ? StatusEtapa.Aprovada
                : StatusEtapa.PontoDeAtencao,
              dataCadastro: moment().format(),
            }).then(() => {
              SnackbarUtils.success(
                languageValues.toasts.success[
                aprovar ? 'etapaAprovada' : 'pontoDeAtencao'
                ]
              );
              scrollToNext.current = true;
              setOpened(false);
            });
          },
        })
      );
    },
    [avaliarEtapa, d, props.etapa, props.flgInfantil]
  );

  const documentos = useMemo(() => {
    return projeto.documentos && projeto.documentos.length
      ? (getProjectFilesByEtapa(
        projeto.documentos,
        indexOfEtapa(props.etapa),
        projeto.id
      ) as DocumentoProjeto[])
      : [];
  }, [projeto.documentos, projeto.id, props.etapa]);

  const previaDocumentos = useMemo(() => {
    const { etapa } = props;
    const idValue = String(etapa);

    const generateThumbnail = (url: string, index: number) => {
      controller.current = new AbortController();
      generateVideoThumbnail(url, controller.current.signal)
        .then((thumb) => {
          const el = document.getElementById(`${idValue}-${index}`);
          if (el) {
            el.style.backgroundImage = `url(${thumb})`;
          }
        })
        .catch((err) => {
          console.error('Não foi possível gerar a miniatura do vídeo', err);
        });
    };

    const createDocumentElement = (disp: any, index: number) => {
      const { caminho, caminhoCompressed, tipo } = disp;
      const baseUrl = getApis().apis.bff.concat('v1/documento/');
      const url = `${baseUrl}${caminho ?? ''}`;
      const urlThumb = `${baseUrl}${caminhoCompressed ?? caminho ?? ''}`;

      if (opened && typeIsVideo(tipo) && (caminhoCompressed ?? caminho)) {
        generateThumbnail(url, index);
      }

      return (
        <div
          key={index}
          id={`${id}-${index}`}
          className={`${rootClassName}-img`}
          style={{
            backgroundImage: typeIsImage(tipo) ? `url(${urlThumb})` : '',
          }}
          onClick={() => {
            window.open(url, '_blank');
          }}
        />
      );
    };

    return documentos.map(createDocumentElement);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, documentos]);

  const name = useMemo(() => {
    const item = languageValues.pages.projetos.etapas[props.etapa].name;
    if (typeof item === 'function') {
      return item(props.flgInfantil);
    }
    return item;
  }, [props.etapa, props.flgInfantil]);

  return (
    <Row className={rootClassName}>
      <Col className={`${rootClassName}-actions-col`}>
        <button
          id={id}
          className={getMergedClassNames([
            // Interfere em comportamentos!
            `${rootClassName}-etapa-button`,
            `status-${status}`,
          ])}
          onClick={() => {
            if (opened && controller.current) {
              controller.current.abort();
            }
            setOpened(!opened);
          }}
        >
          {name}
          {languageValues.pages.projetos.statusEtapa[status]
            ? ` - ${languageValues.pages.projetos.statusEtapa[status]}`
            : undefined}
          <span
            className={getMergedClassNames([
              `${rootClassName}-arrow`,
              opened ? 'opened' : 'closed',
            ])}
          >
            <KeyboardArrowDownIcon />
          </span>
        </button>
      </Col>
      <Collapse
        in={opened}
        timeout={300}
        className={`${rootClassName}-collapse`}
        style={{ width: '100%' }}
        onExited={() => {
          const elements = Array.from<HTMLElement>(
            // Status-1 = Aguardando aprovação
            // Status-3 = Ponto de atenção
            document.querySelectorAll(
              `.review-etapa-etapa-button.status-${StatusEtapa.AguardandoAprovacao},.review-etapa-etapa-button.status-${StatusEtapa.PontoDeAtencao}`
            ) ?? []
          );
          let el: HTMLElement | undefined;

          for (let i = 0; i < elements.length; i++) {
            el = elements[i];
            if (
              el.querySelector('.opened') &&
              el.hasAttribute('id') &&
              el.id !== id
            ) {
              break;
            }
          }

          if (scrollToNext.current && el) {
            scrollToNext.current = false;
            scroller.scrollTo(el?.id, {
              containerId: REVISAO_SCROLL_ID,
              duration: 500,
              delay: 0,
              smooth: true,
              offset: -8,
            });
          }
        }}
      >
        <Col className={`${rootClassName}-fields-row`}>
          {fields.map((f, i) => {
            const red = f.value === languageValues.pages.projetos.notFullfilled;

            return (
              <div key={`${i}_${f.value}`} className={`${rootClassName}-field`}>
                <div className={`${rootClassName}-label`}>{f.label}</div>
                <div
                  className={`${rootClassName}-value ${red ? 'not-fullfilled' : ''
                    }`}
                >
                  {f.value}
                </div>
              </div>
            );
          })}
          {documentos.length > 0 && (
            <div className={`${rootClassName}-field`}>
              <div className={`${rootClassName}-label`}>
                {languageValues.inputs.fotosVideos.label}
              </div>
              <div className={`${rootClassName}-value`}>{previaDocumentos}</div>
            </div>
          )}
        </Col>
        {etapasConfig[props.etapa].needValidation && (
          <Col className={`${rootClassName}-right`}>
            {canApprove && (
              <CustomIconButton
                disabled={disabledFields}
                icon={<LikeIcon />}
                theme='default'
                tooltip={languageValues.pages.projetos.actions.approve}
                onClick={() => {
                  openModalRevisao(true);
                }}
              />
            )}
            {canReprove && (
              <CustomIconButton
                disabled={disabledFields}
                icon={<Dislike />}
                theme='error'
                tooltip={languageValues.pages.projetos.actions.attentionPoint}
                onClick={() => {
                  openModalRevisao(false);
                }}
              />
            )}
            <CustomIconButton
              icon={<HistoryRoundedIcon />}
              theme='warning'
              tooltip={languageValues.pages.projetos.actions.viewComments}
              onClick={() => {
                const modalKey = getUniqueKey();
                d(
                  openModal(HistoricoModal, {
                    historico: statusEtapas[props.etapa]!,
                    modalKey,
                    etapa: props.etapa,
                    flgInfantil: props.flgInfantil,
                  })
                );
              }}
              disabled={
                !(props.etapa in statusEtapas) ||
                !statusEtapas[props.etapa]?.historico?.length
              }
            />
          </Col>
        )}
        <Col>
          <hr />
        </Col>
      </Collapse>
    </Row>
  );
}
